import React, { useCallback } from 'react';
import { useTheme, type ThemeMode, type ColorScheme } from '../contexts/ThemeContext';
import { Check, Sun, Moon, Monitor, SunMoon, Palette } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import { saveUserInfo } from '@/services/userService';
import { useUser } from '@/contexts/UserContextProvider';

// Add glass panel styles to your global CSS or component
const glassPanelStyles = {
  backdropFilter: 'blur(16px)',
  backgroundColor: 'rgba(255, 255, 255, 0.25)',
  boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
  border: '1px solid rgba(255, 255, 255, 0.3)'
};

interface ColorOption {
  name: string;
  value: ColorScheme;
  primaryClass: string;
  accentClass: string;
}

const colorOptions: ColorOption[] = [
  { name: 'Default', value: 'default', primaryClass: 'bg-amber-500', accentClass: 'bg-amber-500' },
  { name: 'Blue', value: 'blue', primaryClass: 'bg-blue-600', accentClass: 'bg-blue-400' },
  { name: 'Purple', value: 'purple', primaryClass: 'bg-purple-600', accentClass: 'bg-purple-400' },
  { name: 'Green', value: 'green', primaryClass: 'bg-green-600', accentClass: 'bg-green-400' },
  { name: 'Orange', value: 'orange', primaryClass: 'bg-orange-600', accentClass: 'bg-orange-400' },
  { name: 'Pink', value: 'pink', primaryClass: 'bg-pink-600', accentClass: 'bg-pink-400' },
  { name: 'Red', value: 'red', primaryClass: 'bg-red-600', accentClass: 'bg-red-400' },
];

export function ThemeSettings() {
  const { user: userOption } = useUser();
  const user = Array.isArray(userOption) ? userOption[0] : userOption;
  const { mode, setMode, primaryColor, setPrimaryColor, accentColor, setAccentColor } = useTheme();

  // Save theme preferences when they change
  const saveThemePreference = useCallback(async (updates: {
    mode?: ThemeMode;
    primaryColor?: ColorScheme;
    accentColor?: ColorScheme;
  }) => {
    if (!user) {
      console.error('No user found, cannot save theme preferences');
      toast.error('You must be logged in to save theme preferences');
      return;
    }

    const toastId = toast.loading('Saving theme preferences...');
    console.log('Saving theme preferences:', updates);

    try {
      const themeUpdate = {
        ...(user.theme || {}),
        ...updates
      };

      console.log('Theme update payload:', { theme: themeUpdate });

      // await saveUserInfo({
      //   theme: themeUpdate
      // });

      console.log('Theme preferences saved successfully');
      toast.success('Theme preferences saved', { id: toastId });
      return true;
    } catch (error) {
      console.error('Failed to save theme preferences:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to save theme preferences';
      toast.error(errorMessage, { id: toastId });
      return false;
    }
  }, [user]);

  const handleModeChange = async (newMode: ThemeMode) => {
    console.log('ThemeSettings.handleModeChange: changing mode from', mode, 'to', newMode, {
      currentMode: mode,
      newMode,
      stackTrace: new Error().stack?.split('\n').slice(1, 5).join('\n')
    });
    setMode(newMode);
    const success = await saveThemePreference({ mode: newMode });
    if (!success) {
      // Revert UI if save fails
      const savedMode = user?.theme?.mode || 'system';
      console.log('ThemeSettings.handleModeChange: reverting to saved mode', savedMode);
      setMode(savedMode as ThemeMode);
    }
  };

  const handlePrimaryColorChange = async (color: ColorScheme) => {
    console.log('Primary color changed to:', color);
    setPrimaryColor(color);
    const success = await saveThemePreference({ primaryColor: color });
    if (!success) {
      // Revert UI if save fails
      const savedColor = user?.theme?.primaryColor || 'default';
      setPrimaryColor(savedColor as ColorScheme);
    }
  };

  const handleAccentColorChange = async (color: ColorScheme) => {
    console.log('Accent color changed to:', color);
    setAccentColor(color);
    const success = await saveThemePreference({ accentColor: color });
    if (!success) {
      // Revert UI if save fails
      const savedColor = user?.theme?.accentColor || 'default';
      setAccentColor(savedColor as ColorScheme);
    }
  };

  return (
    <div className="p-4 rounded-xl border border-border/40 bg-card shadow-sm">
      <div className="mb-4">
        <h2 className="text-xl font-semibold">Theme Customisation</h2>
        <p className="text-sm text-muted-foreground mt-1">Personalise your application's look and feel</p>
      </div>
      <div className="space-y-6">
        <div className="p-4 rounded-lg border border-border/30 bg-background/50">
          <div className="flex items-center gap-2 mb-3">
            <SunMoon className="w-4 h-4 text-primary" />
            <h3 className="text-base font-medium">Colour Scheme</h3>
          </div>
          <p className="text-xs text-muted-foreground mb-3">Choose how the application appearance should be displayed</p>
          <div className="grid grid-cols-3 gap-2">
            {[
              { value: 'light', icon: Sun, label: 'Light' },
              { value: 'dark', icon: Moon, label: 'Dark' },
              { value: 'system', icon: Monitor, label: 'System' }
            ].map(({ value, icon: Icon, label }) => (
              <button
                key={value}
                onClick={() => handleModeChange(value as any)}
                className={`flex flex-col items-center justify-center p-3 rounded-lg transition-colors border ${
                  mode === value 
                    ? 'border-primary bg-primary/5' 
                    : 'border-border hover:bg-foreground/5'
                }`}
              >
                <div className={`p-2 rounded-md mb-1 ${mode === value ? 'bg-primary/10' : 'bg-foreground/5'}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <span className={`text-xs font-medium ${mode === value ? 'text-primary' : 'text-foreground/80'}`}>{label}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="p-4 rounded-lg border border-border/30 bg-background/50">
          <div className="flex items-center gap-2 mb-4">
            <Palette className="w-4 h-4 text-primary" />
            <h3 className="text-base font-medium">Colour Palette</h3>
          </div>
          <div className="space-y-5">
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium">Primary Colour</h4>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-foreground/5 text-foreground/60">
                  {colorOptions.find(c => c.value === primaryColor)?.name || 'Custom'}
                </span>
              </div>
              <div className="grid grid-cols-6 gap-2">
                {colorOptions.map((color) => (
                  <button
                    key={`primary-${color.value}`}
                    onClick={() => handlePrimaryColorChange(color.value)}
                    className={`relative aspect-square rounded-lg transition-transform hover:scale-105 ${
                      color.primaryClass
                    } ${primaryColor === color.value ? 'ring-2 ring-primary/60' : ''}`}
                    title={color.name}
                  />
                ))}
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium">Accent Colour</h4>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-foreground/5 text-foreground/60">
                  {colorOptions.find(c => c.value === accentColor)?.name || 'Custom'}
                </span>
              </div>
              <div className="grid grid-cols-6 gap-2">
                {colorOptions.map((color) => (
                  <button
                    key={`accent-${color.value}`}
                    onClick={() => handleAccentColorChange(color.value)}
                    className={`relative aspect-square rounded-lg transition-transform hover:scale-105 ${
                      color.accentClass
                    } ${accentColor === color.value ? 'ring-2 ring-primary/60' : ''}`}
                    title={color.name}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
